import { useEffect, useState } from "react";

import NoteService from "src/Services/API/note.service";
import UserService from "src/Services/API/user.service";
import useWoCustomerActivity from "src/hooks/useWoCustomerActivity";
import { PENDING_REQUESTS_REFRESH_EVENT } from "src/hooks/usePendingRequestCount";

const REFRESH_MS = 60 * 1000;

// One shared copy for every badge on screen (side menu, list rows, the work
// order page), refreshed together.
// info: each of those work orders' status and location (for counting by
// tab, e.g. the Jobs page's Assigned / Past).
let current = { total: 0, wos: {}, info: {} };
const listeners = new Set();
let inflight = null;

export const refreshChatUnread = () => {
  if (!UserService.getCurrentUser()) return Promise.resolve(current);
  if (inflight) return inflight;
  inflight = NoteService.getUnread()
    .then((response) => {
      current = {
        total: Number(response.data?.total) || 0,
        wos: response.data?.wos || {},
        info: response.data?.info || {},
      };
      listeners.forEach((listener) => listener(current));
      return current;
    })
    .catch(() => current)
    .finally(() => {
      inflight = null;
    });
  return inflight;
};

// The person has read a work order's chat: clear its count everywhere,
// including the side menu's Work Orders number.
export const markChatRead = (woId) =>
  NoteService.markRead(woId)
    .then(() => refreshChatUnread())
    .then(() => window.dispatchEvent(new Event(PENDING_REQUESTS_REFRESH_EVENT)))
    .catch(() => {});

/**
 * The current user's unread customer chat messages, per work order:
 * { total, wos: { [woId]: count } }. Re-checked when a customer sends one
 * (live), every minute, and when the tab comes back into view.
 */
function useChatUnread() {
  const [value, setValue] = useState(current);

  useEffect(() => {
    listeners.add(setValue);
    refreshChatUnread();
    const timer = setInterval(refreshChatUnread, REFRESH_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshChatUnread();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      listeners.delete(setValue);
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  useWoCustomerActivity((activity) => {
    if (activity?.kind === "note") refreshChatUnread();
  });

  return value;
}

export default useChatUnread;
